// AAL-86 MyServices Copilot: the apply step of a Proposal.
//
// `applyProposal` takes the page entity, a Proposal and the review state and returns new copies of
// the entity and the review state plus the operations it dropped. It is pure: no Vue, no store, no
// DOM. Page-specific glue (child re-sync, changed flags, validation, scrolling) lives in the page
// adapter (ServiceDesignerCopilot.js).
//
// Overwrite policy (docs/AAL-86.md, "Proposal"): a set on an empty field is applied at once and
// marked `proposed`; a set on a non-empty field is a `pending` suggestion (old and new value) that
// is written only on accept. Empty means null, undefined, a blank string, an empty list or object,
// an entry without id; 0 and false are values. A newer set for a `proposed` field refines it in
// place only while the field still holds the proposed value; once the user edited it, the newer set
// is a `pending` suggestion against the user's value. Appends are applied at once (a new item is an empty slot); removes
// are always pending because they destroy a value. Reverting restores the exact prior value.

import type { PlaceholderListPredicate, PopulatedFormContract, PopulatedField, PopulatedSection, Proposal, ProposalOperation } from './form-contract'

export type ReviewStatus = 'proposed' | 'pending' | 'accepted' | 'reverted'

export type ReviewEntry = {
	id: string
	op: 'set' | 'append' | 'remove'
	/** Field path for `set`; item path (`<list>.<index>`) for `append` and `remove` */
	path: string
	section: string
	sectionLabel: string
	fieldKey: string
	fieldLabel: string
	status: ReviewStatus
	reason: string
	confidence: number
	/** Exact value before the operation: the old field value, the removed item; undefined for appends */
	previous: unknown
	/** The proposed value: new field value or appended item; undefined for removes */
	value: unknown
	/**
	 * An append that started a placeholder list afresh (the profile copy, the untouched skeleton): that list,
	 * so reverting the last append on it brings the exact placeholder back and the page shows what it showed.
	 */
	replacedPlaceholder?: unknown[]
	/** Keys of an appended item the contract does not know or does not allow; kept for the report */
	ignoredKeys?: string[]
	/** Validation errors of the page, filled by the page adapter */
	errors: string[]
}

export type ReviewState = { entries: ReviewEntry[], nextId: number }

export type DropReason = 'unknownPath' | 'readOnly' | 'listSet' | 'notAList' | 'badIndex' | 'appendOnly' | 'locked' | 'unresolvedEntry' | 'unknownOp'
export type DroppedOperation = { operation: ProposalOperation, reason: DropReason }

export type ApplyOptions = {
	/** Skeleton of a new list item, keyed by the list field key (e.g. `ticketOptions`); defaults to `{}` */
	itemTemplates?: Record<string, () => unknown>
	/**
	 * Turns a Proposal value for an `entry` field (or an item of an entry list) into the entity's entry
	 * object. Return undefined when the value is unknown; the operation is then dropped. The default
	 * accepts entry objects and `{ id, title }` summaries.
	 */
	resolveEntry?: (field: PopulatedField, value: unknown, serviceLocale: string) => unknown
	/**
	 * The page's placeholder predicate, the same one the contract builder got (BuildOptions.isPlaceholderList):
	 * a list that holds nothing of the Service's own counts as empty. A set replaces it (proposed, revert
	 * brings it back) and an append starts it afresh, so item indexes match the empty list the contract showed.
	 */
	isPlaceholderList?: PlaceholderListPredicate
	/**
	 * Items the page does not let the user remove (the home Client, a mandatory sales channel): an identity key
	 * for such an item, undefined for any other. Unlike the contract's `locked` marks it also covers items the
	 * page put into an item the Copilot appended (the mandatory channels of a new client assignment).
	 */
	lockedItemKey?: (field: PopulatedField, item: unknown) => string | undefined
}

export type ApplyResult = { entity: any, reviewState: ReviewState, dropped: DroppedOperation[] }
export type ReviewResult = { entity: any, reviewState: ReviewState }

export function emptyReviewState(): ReviewState {
	return { entries: [], nextId: 1 }
}

export type ReviewOutcome = 'accepted' | 'reverted' | 'pending'

/**
 * The review outcome per field path, as reported to the agent service (ticket 09): `proposed`
 * counts as accepted (the value is in the entity and was not reverted), `pending` stays pending.
 * One entry per path; when several entries share a path the later one wins.
 */
export function reviewOutcomes(entries: Pick<ReviewEntry, 'path' | 'status'>[]): { path: string, outcome: ReviewOutcome }[] {
	const byPath = new Map<string, ReviewOutcome>()
	for (const entry of entries) byPath.set(entry.path, entry.status === 'proposed' ? 'accepted' : entry.status)
	return [ ...byPath ].map(([ path, outcome ]) => ({ path, outcome }))
}

// ---------------------------------------------------------------------------------------
// Apply
// ---------------------------------------------------------------------------------------

export function applyProposal(entity: any, proposal: Proposal, reviewState: ReviewState, contract: PopulatedFormContract, options: ApplyOptions = {}): ApplyResult {
	const result: ApplyResult = { entity: clone(entity), reviewState: cloneState(reviewState), dropped: [] }
	for (const operation of proposal.operations ?? []) {
		const reason = applyOperation(result, operation, contract, options)
		if (reason) result.dropped.push({ operation, reason })
	}
	return result
}

function applyOperation(result: ApplyResult, operation: ProposalOperation, contract: PopulatedFormContract, options: ApplyOptions): DropReason | undefined {
	const target = resolveTarget(contract, operation.path)
	if (!target) return 'unknownPath'
	if (!target.field.writable) return 'readOnly'
	if (missingListItem(result.entity, operation.path)) return 'badIndex'
	switch (operation.op) {
		case 'set': return applySet(result, operation, target, contract, options)
		case 'append': return applyAppend(result, operation, target, contract, options)
		case 'remove': return applyRemove(result, operation, target, options)
		default: return 'unknownOp'
	}
}

function applySet(result: ApplyResult, operation: Extract<ProposalOperation, { op: 'set' }>, target: Target, contract: PopulatedFormContract, options: ApplyOptions): DropReason | undefined {
	const { field } = target
	let value = operation.value
	const current = readPath(result.entity, operation.path)
	if (field.type === 'list') {
		if (!listIsReplaceable(result.reviewState, operation.path, current) && !isPlaceholder(field, current, result.entity, options)) return 'listSet'
		value = buildList(field, value, contract.serviceLocale, options)
		if (value === undefined) return 'unresolvedEntry'
		value = keepLockedItems(field, current, value as unknown[], options)
	}
	if (field.type === 'entry') {
		value = resolveEntry(field, value, contract.serviceLocale, options)
		if (value === undefined) return 'unresolvedEntry'
	}
	const entries = result.reviewState.entries
	const existingIndex = entries.findIndex(e => e.op === 'set' && e.path === operation.path)
	const existing = existingIndex >= 0 ? entries[existingIndex] : undefined
	// A newer suggestion for the same field replaces the older one. While the field still holds the
	// older proposed value it is refined in place and keeps the original prior value; anything else
	// (edited by hand, pending, accepted, reverted) is judged on the field's current value.
	const refinesUntouched = existing?.status === 'proposed' && sameValue(current, existing.value)
	const previous = refinesUntouched ? existing!.previous : clone(current)
	const pending = refinesUntouched ? false : existing?.status === 'pending' || existing?.status === 'proposed' || (field.type !== 'list' && !isEmpty(field, current))
	if (existing) entries.splice(existingIndex, 1)
	const entry = newEntry(result.reviewState, operation, target, { path: operation.path, previous, value: clone(value), status: pending ? 'pending' : 'proposed' })
	if (!pending) writePath(result.entity, operation.path, clone(value))
	entries.push(entry)
	return undefined
}

function applyAppend(result: ApplyResult, operation: Extract<ProposalOperation, { op: 'append' }>, target: Target, contract: PopulatedFormContract, options: ApplyOptions): DropReason | undefined {
	const { field } = target
	if (field.type !== 'list') return 'notAList'
	const ignoredKeys: string[] = []
	let item: unknown
	if (field.items) {
		item = buildItem(field, operation.item, contract.serviceLocale, options, ignoredKeys)
	}
	else {
		item = resolveEntry(field, operation.item, contract.serviceLocale, options)
		if (item === undefined) return 'unresolvedEntry'
	}
	let list = readPath(result.entity, operation.path)
	// items the Copilot appended earlier make the list its own, even where the page predicate still calls it a
	// placeholder (the copy switch stays on until the user accepts switching it off)
	const ownItems = result.reviewState.entries.some(e => e.op === 'append' && e.status !== 'reverted' && splitItemPath(e.path).listPath === operation.path)
	const placeholder = !ownItems && isPlaceholder(field, list, result.entity, options) ? clone(list) : undefined
	if (!Array.isArray(list) || placeholder) {
		list = []
		writePath(result.entity, operation.path, list)
	}
	const index = list.length
	list.push(item)
	const entry = newEntry(result.reviewState, operation, target, { path: `${operation.path}.${index}`, previous: undefined, value: clone(item), status: 'proposed' })
	if (placeholder) entry.replacedPlaceholder = placeholder
	if (ignoredKeys.length) entry.ignoredKeys = ignoredKeys
	result.reviewState.entries.push(entry)
	return undefined
}

function applyRemove(result: ApplyResult, operation: Extract<ProposalOperation, { op: 'remove' }>, target: Target, options: ApplyOptions): DropReason | undefined {
	const { field } = target
	if (field.type !== 'list') return 'notAList'
	if (field.writeMode === 'append') return 'appendOnly'
	const list = readPath(result.entity, operation.path)
	if (!Array.isArray(list) || !Number.isInteger(operation.index) || operation.index < 0 || operation.index >= list.length) return 'badIndex'
	// the page does not let the user remove this item (contract value marked `locked`, or the page says so)
	if (Array.isArray(field.value) && (field.value[operation.index] as any)?.locked === true) return 'locked'
	if (options.lockedItemKey?.(field, list[operation.index]) !== undefined) return 'locked'
	const path = `${operation.path}.${operation.index}`
	if (result.reviewState.entries.some(e => e.op === 'remove' && e.path === path && e.status !== 'reverted')) return undefined
	const entry = newEntry(result.reviewState, operation, target, { path, previous: clone(list[operation.index]), value: undefined, status: 'pending' })
	result.reviewState.entries.push(entry)
	return undefined
}

// A whole list may be replaced only when that loses nothing the user typed (ticket 17): the list is
// empty, or it lies inside an item the Copilot built and still holds what that operation put there (the
// page skeleton fills new availabilities with all weekdays and one contingent, new business-hours periods
// with closed days). Items the Copilot built are the ones it appended (ticket 17) and the items of a whole
// list it set (ticket 20: own business hours set as a list, then the times of one day set inside a period;
// see "sets the times of a day inside a list it set" in proposal-apply.spec.js). Locked items are kept
// either way (keepLockedItems).
function listIsReplaceable(state: ReviewState, path: string, current: unknown): boolean {
	if (isEmpty({ type: 'list' }, current)) return true
	return state.entries.some(entry => builtItems(entry) && entry.status !== 'reverted' && entry.status !== 'pending'
		&& path.startsWith(`${entry.path}.`) && sameValue(current, readPath(entry.value, path.slice(entry.path.length + 1))))
}

function builtItems(entry: ReviewEntry): boolean {
	return entry.op === 'append' || (entry.op === 'set' && Array.isArray(entry.value))
}

// A whole-list set never loses a locked item: the ones the new list lacks are kept in front of it.
function keepLockedItems(field: PopulatedField, current: unknown, items: unknown[], options: ApplyOptions): unknown[] {
	const keyOf = options.lockedItemKey
	if (!keyOf || !Array.isArray(current)) return items
	const kept = new Set(items.map(item => keyOf(field, item)).filter(key => key !== undefined))
	const missing = current.filter(item => {
		const key = keyOf(field, item)
		return key !== undefined && !kept.has(key)
	})
	return missing.length ? [ ...clone(missing), ...items ] : items
}

function isPlaceholder(field: PopulatedField, current: unknown, entity: any, options: ApplyOptions): boolean {
	return Array.isArray(current) && current.length > 0 && options.isPlaceholderList?.(field, current, entity) === true
}

// The items of a whole-list set, built like appended items; undefined when one cannot be resolved.
function buildList(field: PopulatedField, raw: unknown, locale: string, options: ApplyOptions): unknown[] | undefined {
	if (raw === null || raw === undefined) return []
	if (!Array.isArray(raw)) return undefined
	const items: unknown[] = []
	for (const entry of raw) {
		const item = field.items ? buildItem(field, entry, locale, options, []) : resolveEntry(field, entry, locale, options)
		if (item === undefined) return undefined
		items.push(item)
	}
	return items
}

// Builds one list item from a Proposal item keyed by sub-field key, on top of the page's skeleton.
function buildItem(field: PopulatedField, raw: unknown, locale: string, options: ApplyOptions, ignoredKeys: string[]): unknown {
	const item: any = clone(options.itemTemplates?.[field.key]?.() ?? {})
	const source = raw && typeof raw === 'object' ? raw as Record<string, unknown> : {}
	for (const [ key, rawValue ] of Object.entries(source)) {
		const sub = field.items?.find(s => s.key === key)
		if (!sub || !sub.writable) { ignoredKeys.push(key); continue }
		let value: unknown = rawValue
		if (sub.type === 'entry') {
			value = resolveEntry(sub, rawValue, locale, options)
			if (value === undefined) { ignoredKeys.push(key); continue }
		}
		else if (sub.type === 'list' && sub.items && Array.isArray(rawValue)) {
			value = rawValue.map(nested => buildItem(sub, nested, locale, options, ignoredKeys))
		}
		writePath(item, sub.path, clone(value))
	}
	return item
}

function resolveEntry(field: PopulatedField, value: unknown, locale: string, options: ApplyOptions): unknown {
	if (options.resolveEntry) return options.resolveEntry(field, value, locale)
	return defaultResolveEntry(value, locale)
}

// Without page choices, an entry object passes through and an `{ id, title }` summary becomes a
// minimal entry. A bare id cannot be resolved here.
export function defaultResolveEntry(value: unknown, locale: string): unknown {
	if (value === null) return null
	if (typeof value !== 'object') return undefined
	const candidate = value as any
	if (typeof candidate.sys?.id === 'string') return candidate
	if (typeof candidate.id === 'string') {
		const entry: any = { sys: { id: candidate.id } }
		if (candidate.title !== undefined) entry.fields = { title: { [locale]: candidate.title } }
		return entry
	}
	return undefined
}

// ---------------------------------------------------------------------------------------
// Review: accept and revert
// ---------------------------------------------------------------------------------------

export function acceptField(entity: any, reviewState: ReviewState, entryId: string): ReviewResult {
	const result: ReviewResult = { entity: clone(entity), reviewState: cloneState(reviewState) }
	acceptInPlace(result, entryId)
	return result
}

export function revertField(entity: any, reviewState: ReviewState, entryId: string): ReviewResult {
	const result: ReviewResult = { entity: clone(entity), reviewState: cloneState(reviewState) }
	revertInPlace(result, entryId)
	return result
}

export function acceptAll(entity: any, reviewState: ReviewState): ReviewResult {
	const result: ReviewResult = { entity: clone(entity), reviewState: cloneState(reviewState) }
	for (const entry of result.reviewState.entries) {
		if (entry.status === 'pending' || entry.status === 'proposed') acceptInPlace(result, entry.id)
	}
	return result
}

export function revertAll(entity: any, reviewState: ReviewState): ReviewResult {
	const result: ReviewResult = { entity: clone(entity), reviewState: cloneState(reviewState) }
	for (const entry of result.reviewState.entries) {
		if (entry.status !== 'reverted') revertInPlace(result, entry.id)
	}
	return result
}

function acceptInPlace(result: ReviewResult, entryId: string): void {
	const entry = result.reviewState.entries.find(e => e.id === entryId)
	if (!entry || entry.status === 'accepted') return
	const { listPath, index } = splitItemPath(entry.path)
	switch (entry.op) {
		case 'set':
			writePath(result.entity, entry.path, clone(entry.value))
			break
		case 'append':
			if (entry.status === 'reverted') {
				// the placeholder came back when this append was reverted: start the list afresh again
				const placeholder = placeholderOf(result.reviewState.entries, listPath)
				if (placeholder && sameValue(readPath(result.entity, listPath), placeholder)) writePath(result.entity, listPath, [])
				listAt(result.entity, listPath).splice(index, 0, clone(entry.value))
				shiftIndices(result.reviewState.entries, entry, listPath, index, +1)
			}
			break
		case 'remove':
			listAt(result.entity, listPath).splice(index, 1)
			shiftIndices(result.reviewState.entries, entry, listPath, index, -1)
			break
	}
	entry.status = 'accepted'
}

function revertInPlace(result: ReviewResult, entryId: string): void {
	const entry = result.reviewState.entries.find(e => e.id === entryId)
	if (!entry || entry.status === 'reverted') return
	const { listPath, index } = splitItemPath(entry.path)
	switch (entry.op) {
		case 'set':
			if (entry.status !== 'pending') writePath(result.entity, entry.path, clone(entry.previous))
			break
		case 'append': {
			const list = listAt(result.entity, listPath)
			list.splice(index, 1)
			shiftIndices(result.reviewState.entries, entry, listPath, index, -1)
			// the last Copilot item is gone: the list holds what it held before, the placeholder included
			const placeholder = placeholderOf(result.reviewState.entries, listPath)
			if (!list.length && placeholder) writePath(result.entity, listPath, clone(placeholder))
			break
		}
		case 'remove':
			if (entry.status === 'accepted') {
				listAt(result.entity, listPath).splice(index, 0, clone(entry.previous))
				shiftIndices(result.reviewState.entries, entry, listPath, index, +1)
			}
			break
	}
	entry.status = 'reverted'
}

// The placeholder list the latest append on `listPath` replaced, if any.
function placeholderOf(entries: ReviewEntry[], listPath: string): unknown[] | undefined {
	for (let i = entries.length - 1; i >= 0; i--) {
		const entry = entries[i]
		if (entry.op === 'append' && entry.replacedPlaceholder && splitItemPath(entry.path).listPath === listPath) return entry.replacedPlaceholder
	}
	return undefined
}

// Keeps the paths of the other entries on the same list pointing at the same items after an insert
// or a splice at `index`. Reverted entries are not shifted: their index is only meaningful when
// they are re-applied, which re-reads the list.
function shiftIndices(entries: ReviewEntry[], origin: ReviewEntry, listPath: string, index: number, delta: number): void {
	const prefix = `${listPath}.`
	for (const entry of entries) {
		if (entry === origin || !entry.path.startsWith(prefix)) continue
		const rest = entry.path.slice(prefix.length)
		const match = /^(\d+)(.*)$/.exec(rest)
		if (!match) continue
		const itemIndex = Number(match[1])
		const affected = delta > 0 ? itemIndex >= index : itemIndex > index
		if (!affected) continue
		entry.path = `${prefix}${itemIndex + delta}${match[2]}`
	}
}

// ---------------------------------------------------------------------------------------
// Contract lookup
// ---------------------------------------------------------------------------------------

type Target = { section: PopulatedSection, field: PopulatedField }

function resolveTarget(contract: PopulatedFormContract, path: string): Target | undefined {
	if (typeof path !== 'string' || !path) return undefined
	for (const section of contract.sections) {
		for (const field of section.fields) {
			const hit = matchField(field, field.path, path)
			if (hit) return { section, field: hit }
		}
	}
	return undefined
}

// Walks list items by numeric index; inside an item the sub-field paths are relative to the item.
function matchField(field: PopulatedField, absolutePath: string, target: string): PopulatedField | undefined {
	if (target === absolutePath) return field
	if (field.type !== 'list' || !field.items || !target.startsWith(`${absolutePath}.`)) return undefined
	const rest = target.slice(absolutePath.length + 1).split('.')
	if (!/^\d+$/.test(rest[0]) || rest.length === 1) return undefined
	const itemPath = `${absolutePath}.${rest[0]}`
	for (const sub of field.items) {
		const hit = matchField(sub, `${itemPath}.${sub.path}`, target)
		if (hit) return hit
	}
	return undefined
}

// ---------------------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------------------

export function isEmpty(field: Pick<PopulatedField, 'type'>, value: unknown): boolean {
	if (value === undefined || value === null) return true
	if (typeof value === 'string') return value.trim() === ''
	if (Array.isArray(value)) return value.length === 0
	// 0 and false are values (a lead time of 0 hours, a price of 0, request-only off), never empty.
	if (typeof value === 'number' || typeof value === 'boolean') return false
	if (field.type === 'entry') return !(value as any)?.sys?.id
	if (typeof value === 'object') return Object.keys(value as object).length === 0
	return false
}

function sameValue(a: unknown, b: unknown): boolean {
	return JSON.stringify(a) === JSON.stringify(b)
}

function newEntry(state: ReviewState, operation: ProposalOperation, target: Target, part: Pick<ReviewEntry, 'path' | 'previous' | 'value' | 'status'>): ReviewEntry {
	return {
		id: `p${state.nextId++}`,
		op: operation.op,
		section: target.section.key,
		sectionLabel: target.section.label,
		fieldKey: target.field.key,
		fieldLabel: target.field.label,
		reason: operation.reason,
		confidence: operation.confidence,
		errors: [],
		...part,
	}
}

function splitItemPath(path: string): { listPath: string, index: number } {
	const at = path.lastIndexOf('.')
	return { listPath: path.slice(0, at), index: Number(path.slice(at + 1)) }
}

function listAt(root: unknown, path: string): any[] {
	let list = readPath(root, path)
	if (!Array.isArray(list)) {
		list = []
		writePath(root, path, list)
	}
	return list
}

export function readPath(root: unknown, path: string): any {
	return path.split('.').reduce<any>((node, segment) => node?.[segment], root)
}

// Whether the path runs through a list item the entity does not hold: writing it would create an item
// with nothing but the written field (a client assignment without its Client crashes the page).
function missingListItem(root: unknown, path: string): boolean {
	const segments = path.split('.')
	let node: any = root
	for (let i = 0; i < segments.length - 1; i++) {
		if (Array.isArray(node) && !(Number(segments[i]) < node.length)) return true
		node = node?.[segments[i]]
		// below a missing node every index would be created too
		if (node === undefined || node === null) return segments.slice(i + 1, -1).some(segment => /^\d+$/.test(segment))
	}
	return false
}

export function writePath(root: any, path: string, value: unknown): void {
	const segments = path.split('.')
	let node = root
	for (let i = 0; i < segments.length - 1; i++) {
		const segment = segments[i]
		if (node[segment] === undefined || node[segment] === null) {
			node[segment] = /^\d+$/.test(segments[i + 1]) ? [] : {}
		}
		node = node[segment]
	}
	const last = segments[segments.length - 1]
	if (value === undefined) delete node[last]
	else node[last] = value
}

function clone<T>(value: T): T {
	return value === undefined ? value : JSON.parse(JSON.stringify(value))
}

function cloneState(state: ReviewState): ReviewState {
	return { entries: clone(state.entries ?? []), nextId: state.nextId ?? 1 }
}
